/**
 * geocoding.ts
 *
 * Offline name → lat/lon lookup. The ~1,082-entry dataset lives in
 * `assets/deckorate-assets/json/geocoding.json` (not the JS bundle) and is
 * lazy-loaded once into a module-level singleton index on first use. Pure logic
 * apart from the one `fetch` of the JSON asset.
 */

import type { GeoEntry, SearchOptions, SearchResult } from "../types";

/** Higher = more important when scores tie. */
const TYPE_PRIORITY: Record<string, number> = {
  capital: 4,
  city: 3,
  state: 2,
  country: 1,
};

/**
 * Levenshtein edit distance, two-row, short-circuited once the best possible
 * distance on a row exceeds `max` (we only ever care about ≤ 2).
 */
export function levenshtein(a: string, b: string, max = Infinity): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  if (Math.abs(m - n) > max) return max + 1;

  let prev = new Array<number>(n + 1);
  let curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = curr[0]!;
    const ai = a.codePointAt(i - 1);
    for (let j = 1; j <= n; j++) {
      const cost = ai === b.codePointAt(j - 1) ? 0 : 1;
      const del = prev[j]! + 1;
      const ins = curr[j - 1]! + 1;
      const sub = prev[j - 1]! + cost;
      const v = Math.min(del, ins, sub);
      curr[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[n]!;
}

/** Parse "40.7, -74.0" style input → {lat, lon}, validated, or null. */
export function parseCoordinate(
  input: string,
): { lat: number; lon: number } | null {
  if (!input || typeof input !== "string") return null;
  const trimmed = input.trim();
  const pattern = /^(-?\d+(?:\.\d+)?)(?:\s*,\s*|\s+)(-?\d+(?:\.\d+)?)$/;
  const match = pattern.exec(trimmed);
  if (!match) return null;
  const lat = Number.parseFloat(match[1]!);
  const lon = Number.parseFloat(match[2]!);
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

/** An indexed entry with its lowercased name precomputed. */
interface IndexedEntry extends GeoEntry {
  nameLower: string;
  aliasesLower: string[];
}

/** A candidate hit accumulated during a search. */
interface Candidate {
  entry: IndexedEntry;
  score: number;
}

/** Score tiers for one substring-match source, in priority order. */
interface MatchScores {
  exact: number;
  prefix: number;
  contains: number;
  contained: number;
}

const NAME_SCORES: MatchScores = { exact: 100, prefix: 80, contains: 60, contained: 50 };
const ALIAS_SCORES: MatchScores = { exact: 95, prefix: 75, contains: 55, contained: 45 };

/**
 * Score `candidate` against the query `q`: exact > prefix > candidate contains
 * q > q contains candidate (short names only). Null when neither contains the
 * other.
 */
function substringMatchScore(
  candidate: string,
  q: string,
  scores: MatchScores,
): number | null {
  const containsQ = candidate.includes(q);
  const qContainsCandidate =
    candidate.length >= 3 && candidate.length >= q.length * 0.5 && q.includes(candidate);
  if (!containsQ && !qContainsCandidate) return null;
  if (candidate === q) return scores.exact;
  if (candidate.startsWith(q)) return scores.prefix;
  return containsQ ? scores.contains : scores.contained;
}

/** Order candidates: score desc, then type priority desc, then name asc. */
function compareCandidates(a: Candidate, b: Candidate): number {
  if (b.score !== a.score) return b.score - a.score;
  const aPri = TYPE_PRIORITY[a.entry.type] ?? 0;
  const bPri = TYPE_PRIORITY[b.entry.type] ?? 0;
  if (bPri !== aPri) return bPri - aPri;
  return a.entry.name.localeCompare(b.entry.name);
}

export class GeocodingDatabase {
  private readonly index = new Map<string, IndexedEntry[]>();
  private readonly entries: IndexedEntry[];

  constructor(rawEntries: GeoEntry[]) {
    this.entries = rawEntries.map((e) => ({
      ...e,
      nameLower: e.name.toLowerCase(),
      aliasesLower: (e.aliases ?? []).map((a) => a.toLowerCase()),
    }));

    for (const entry of this.entries) {
      this.addToIndex(entry.nameLower, entry);
      for (const alias of entry.aliasesLower) this.addToIndex(alias, entry);
    }
  }

  private addToIndex(key: string, entry: IndexedEntry): void {
    const bucket = this.index.get(key);
    if (bucket) bucket.push(entry);
    else this.index.set(key, [entry]);
  }

  /** Search for locations matching a query string. */
  search(query: string, options: SearchOptions = {}): SearchResult[] {
    const { limit = 10, types, countries } = options;
    if (!query || typeof query !== "string") return [];
    const q = query.trim().toLowerCase();
    if (q.length === 0) return [];

    const coord = parseCoordinate(query);
    if (coord) {
      return [
        {
          name: `${coord.lat}, ${coord.lon}`,
          lat: coord.lat,
          lon: coord.lon,
          type: "coordinate",
          country: null,
          state: null,
          matchScore: 1,
        },
      ];
    }

    const seen = new Map<IndexedEntry, Candidate>();
    const add = (entry: IndexedEntry, score: number): void =>
      this.addCandidate(seen, entry, score, types, countries);

    // 1. Exact index match.
    const exact = this.index.get(q);
    if (exact) for (const entry of exact) add(entry, 100);

    // 2. Substring scan over all names and aliases.
    this.collectSubstringCandidates(q, add);

    // 3. Fuzzy (Levenshtein ≤ 2), only if we still have room.
    if (seen.size < limit && q.length >= 3) {
      this.collectFuzzyCandidates(q, add);
    }

    const results = Array.from(seen.values());
    results.sort(compareCandidates);

    return results.slice(0, limit).map(({ entry, score }) => ({
      name: entry.name,
      lat: entry.lat,
      lon: entry.lon,
      type: entry.type,
      country: entry.country,
      state: entry.state,
      matchScore: score,
    }));
  }

  /** Record `entry` at `score` unless filtered out or already seen better. */
  private addCandidate(
    seen: Map<IndexedEntry, Candidate>,
    entry: IndexedEntry,
    score: number,
    types?: string[],
    countries?: string[],
  ): void {
    if (types && types.length > 0 && !types.includes(entry.type)) return;
    if (
      countries &&
      countries.length > 0 &&
      (entry.country == null || !countries.includes(entry.country))
    ) {
      return;
    }
    const existing = seen.get(entry);
    if (!existing || existing.score < score) seen.set(entry, { entry, score });
  }

  private collectSubstringCandidates(
    q: string,
    add: (entry: IndexedEntry, score: number) => void,
  ): void {
    for (const entry of this.entries) {
      const nameScore = substringMatchScore(entry.nameLower, q, NAME_SCORES);
      if (nameScore != null) add(entry, nameScore);
      for (const aliasLower of entry.aliasesLower) {
        const aliasScore = substringMatchScore(aliasLower, q, ALIAS_SCORES);
        if (aliasScore != null) add(entry, aliasScore);
      }
    }
  }

  private collectFuzzyCandidates(
    q: string,
    add: (entry: IndexedEntry, score: number) => void,
  ): void {
    for (const [key, entries] of this.index) {
      if (key.length > q.length + 2 || key.length < q.length - 2) continue;
      const dist = levenshtein(q, key, 2);
      if (dist > 0 && dist <= 2) {
        const score = 40 - dist * 10;
        for (const entry of entries) add(entry, score);
      }
    }
  }

  /** Case-insensitive exact lookup by name or alias; single best entry or null. */
  getByExactName(name: string): SearchResult | null {
    if (!name || typeof name !== "string") return null;
    const q = name.trim().toLowerCase();
    const entries = this.index.get(q);
    if (!entries || entries.length === 0) return null;
    const sorted = [...entries].sort(
      (a, b) => (TYPE_PRIORITY[b.type] ?? 0) - (TYPE_PRIORITY[a.type] ?? 0),
    );
    const e = sorted[0]!;
    return {
      name: e.name,
      lat: e.lat,
      lon: e.lon,
      type: e.type,
      country: e.country,
      state: e.state,
      matchScore: 100,
    };
  }

  /**
   * Resolve free text to a canonical 2-letter ISO country code (uppercase), or
   * null. Used to fix region shading on the world map, whose shapes are named
   * by ISO code ("AU") — so "Australia" must normalise before matching.
   */
  resolveToCanonicalCountry(input: string): string | null {
    const hit = this.getByExactName(input);
    return hit?.country ?? null;
  }
}

/* ── Lazy singleton ──────────────────────────────── */

let dbPromise: Promise<GeocodingDatabase> | null = null;

/** URL of the lazily-loaded dataset (served from web-addin `public/`). */
const GEOCODING_URL = "/assets/deckorate-assets/json/geocoding.json";

/** Get the shared geocoder, loading + indexing the dataset on first call. */
export function getGeocoder(): Promise<GeocodingDatabase> {
  dbPromise ??= (async () => {
    const res = await fetch(GEOCODING_URL);
    if (!res.ok) {
      throw new Error(
        `Failed to load geocoding data: ${res.status} ${res.statusText}`,
      );
    }
    const entries = (await res.json()) as GeoEntry[];
    return new GeocodingDatabase(entries);
  })();
  return dbPromise;
}
